import React from 'react';

import type { AuditLogDTO, CompanyInfoDTO } from '../../services/apiClient';

import Table from '../../components/common/Table';
import { truncateString } from '../../utils/stringUtils';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';

interface AuditLogTableProps {
  data: AuditLogDTO[];
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
}

const AuditLogTable: React.FC<AuditLogTableProps> = ({
  data,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
}) => {
  // Group changes made in the same save together, ignoring millisecond-level
  // differences between the individual CreatedUtc timestamps of each property change.
  const groupedData = data.map((row) => ({
    ...row,
    groupCreatedUtc: row.createdUtc
      ? new Date(row.createdUtc).toISOString().slice(0, 19)
      : '',
  }));

  return (
    <Table
      columns={[
        {
          label: 'Action',
          key: 'action',
          render: (row: AuditLogDTO) => (
            <span
              className={`inline-block px-2 py-1 text-xs font-bold rounded ${
                row.action === 'Create'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-yellow-100 text-yellow-700'
              }`}
            >
              {row.action}
            </span>
          ),
        },
        { label: 'Property', key: 'propertyName' },
        {
          label: 'Old Value',
          key: 'oldValue',
          render: (row: AuditLogDTO) => (
            <span className="line-through text-red-500">
              {truncateString(row.oldValue ?? '', 50)}
            </span>
          ),
        },
        {
          label: 'New Value',
          key: 'newValue',
          render: (row: AuditLogDTO) => (
            <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded font-bold">
              {truncateString(row.newValue ?? '', 50)}
            </span>
          ),
        },
      ]}
      data={groupedData}
      groupBy={['groupCreatedUtc', 'tableName', 'recordId', 'changedBy']}
      groupHeaderRender={(group, rows) => {
        const [date, tableName, recordId, changedBy] = group.split(' - ');

        return (
          <>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-800 dark:text-gray-200">
                {convertToUserTime(date)} - {tableName}
              </span>
              <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                Record ID: {recordId}
              </span>
              {changedBy && (
                <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                  by {changedBy}
                </span>
              )}
            </div>
            <span className="ml-auto text-sm text-gray-500 hidden sm:block">
              {rows.length} change(s)
            </span>
          </>
        );
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
    />
  );
};

export default AuditLogTable;
